import { newBlockRaw, newWorkbookParameterRaw, readWorkbookBlock, serializeWorkbookBlock } from '@bloo-team/api-client'
import type { BlockKind, WorkbookBlock, WorkbookDoc, WorkbookParameter } from '@bloo-team/api-client'
import { parametersWire, profileOf, withDocMeta, withParameters } from './workbook-blocks'

/**
 * The Edit-mode draft of a workbook: its blocks, name and description, and
 * the Settings tab's Placement (folder path, stage, profile) and Parameters.
 * One Save… writes all of it as one version. The run mode is NOT in the
 * draft — it saves on its own, at once (lib/workbook-schedule.ts).
 *
 * The draft is created on the first switch to Edit and lives until Save or
 * Discard — switching back to View keeps it (the page shows the draft read-only),
 * so a half-made edit is never lost to the mode toggle.
 */
export interface WorkbookDraft {
  blocks: WorkbookBlock[]
  name: string
  description: string
  /** Folder path, '/'-separated with a leading slash. */
  path: string
  stage: string
  /** '' = none (saved as null). */
  profile: string
  parameters: WorkbookParameter[]
}

/** What a draft starts from: the document with its visible blocks. */
export function draftBaseOf(doc: WorkbookDoc, blocks: WorkbookBlock[]): WorkbookDraft {
  return {
    blocks,
    name: doc.name,
    description: doc.description,
    path: doc.path,
    stage: doc.stage,
    profile: profileOf(doc),
    parameters: doc.parameters,
  }
}

export function draftOf(doc: WorkbookDraft): WorkbookDraft {
  return {
    ...doc,
    blocks: doc.blocks.map((b) => ({ ...b, raw: structuredClone(b.raw) })),
    parameters: doc.parameters.map((p) => ({ ...p, ...(p.raw ? { raw: structuredClone(p.raw) } : {}) })),
  }
}

/** A new, empty block of this kind, as the editor starts it. */
export function newDraftBlock(kind: BlockKind): WorkbookBlock {
  return readWorkbookBlock(newBlockRaw(kind)) as WorkbookBlock
}

export function insertBlock(blocks: WorkbookBlock[], at: number, kind: BlockKind): WorkbookBlock[] {
  const i = Math.max(0, Math.min(at, blocks.length))
  return [...blocks.slice(0, i), newDraftBlock(kind), ...blocks.slice(i)]
}

export function removeBlock(blocks: WorkbookBlock[], i: number): WorkbookBlock[] {
  return blocks.filter((_, j) => j !== i)
}

/** Swap with the neighbour; out-of-range moves return the same array. */
export function moveBlock(blocks: WorkbookBlock[], i: number, dir: -1 | 1): WorkbookBlock[] {
  const j = i + dir
  const a = blocks[i]
  const b = blocks[j]
  if (!a || !b) return blocks
  const next = blocks.slice()
  next[i] = b
  next[j] = a
  return next
}

export function patchBlock(blocks: WorkbookBlock[], i: number, next: WorkbookBlock): WorkbookBlock[] {
  return blocks.map((b, j) => (j === i ? next : b))
}

/**
 * Dirty = the draft would save something different from the document. Compared
 * on the SERIALIZED wire form, so an edit typed and typed back is clean again.
 */
export function isDraftDirty(doc: WorkbookDraft, draft: WorkbookDraft | null): boolean {
  if (!draft) return false
  if (draft.name !== doc.name || draft.description !== doc.description) return true
  if (draft.path !== doc.path || draft.stage !== doc.stage || draft.profile !== doc.profile) return true
  if (parametersWire(draft.parameters) !== parametersWire(doc.parameters)) return true
  const wire = (bs: WorkbookBlock[]) => JSON.stringify(bs.map(serializeWorkbookBlock))
  return wire(draft.blocks) !== wire(doc.blocks)
}

/* -------------------------------------------------------------- settings */

/**
 * The draft's Placement and Parameters applied onto `doc` — the CURRENT
 * latest document, so a run-mode change applied since the draft began is
 * kept. Only what differs from `doc` is written; type / cron / cron_obj /
 * timezones are never touched.
 */
export function withDraftSettings(doc: WorkbookDoc, draft: WorkbookDraft): WorkbookDoc {
  const meta: { stage?: string; profile?: string; folder?: string } = {}
  if (draft.path !== doc.path) meta.folder = draft.path
  if (draft.stage !== doc.stage) meta.stage = draft.stage
  if (draft.profile !== profileOf(doc)) meta.profile = draft.profile
  let next = Object.keys(meta).length ? withDocMeta(doc, meta) : doc
  if (parametersWire(draft.parameters) !== parametersWire(doc.parameters)) next = withParameters(next, draft.parameters)
  return next
}

/** A new parameter row, in the legacy new-row shape. */
export function addParameter(ps: WorkbookParameter[]): WorkbookParameter[] {
  return [...ps, { name: '', value: '', raw: newWorkbookParameterRaw() }]
}

export function patchParameter(
  ps: WorkbookParameter[],
  i: number,
  patch: Partial<Pick<WorkbookParameter, 'name' | 'value'>>,
): WorkbookParameter[] {
  return ps.map((p, j) => (j === i ? { ...p, ...patch } : p))
}

export function removeParameter(ps: WorkbookParameter[], i: number): WorkbookParameter[] {
  return ps.filter((_, j) => j !== i)
}

/**
 * Why the draft's parameters cannot save; empty = fine. Either correct
 * parameters or none: each needs a name and a Default Value, and names are
 * unique (queries reference them as `{{Name}}`).
 */
export function parameterProblems(ps: readonly WorkbookParameter[]): string[] {
  const problems: string[] = []
  if (ps.some((p) => p.name.trim() === '')) problems.push('parameter name missing')
  if (ps.some((p) => p.value.trim() === '')) problems.push('parameter default value missing')
  const names = ps.map((p) => p.name.trim()).filter(Boolean)
  const dupes = [...new Set(names.filter((n, i) => names.indexOf(n) !== i))]
  if (dupes.length) problems.push(`duplicate parameter name ${dupes.join(', ')}`)
  return problems
}

export function parametersComplete(ps: readonly WorkbookParameter[]): boolean {
  return parameterProblems(ps).length === 0
}
